import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import api from '../api';
import { logger } from '../utils/logger';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionMeta, setSessionMeta] = useState(null);

  // Check auth via /api/auth/me — httpOnly cookie is sent automatically
  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      const { authProvider, sessionExpiresAt, serverTime, ...userData } = res.data;
      setUser(userData);
      setSessionMeta({ authProvider, sessionExpiresAt, serverTime, lastChecked: new Date().toISOString() });
    } catch (err) {
      logger.error('Auth check failed:', err);
      setUser(null);
      setSessionMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip auto-check on OAuth callback page — AuthCallback handles auth itself
    if (window.location.pathname.startsWith('/auth/')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    // Seed the user's timezone on first registration from the browser.
    let timezone = 'UTC';
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (err) { logger.warn('Browser timezone detection failed, defaulting to UTC:', err?.message); }
    const res = await api.post('/auth/register', { email, password, name, timezone });
    setUser(res.data.user);
    return res.data;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) { logger.error('Logout endpoint failed (may already be logged out):', err); }
    // Privacy: wipe all PolkDesQ-prefixed sessionStorage keys (command history may
    // contain client/matter names) so a shared workstation's next user can't see them.
    // sessionStorage only auto-clears on tab close — logout-in-tab must clear explicitly.
    try {
      const store = window.sessionStorage;
      for (let i = store.length - 1; i >= 0; i -= 1) {
        const key = store.key(i);
        if (key && key.toLowerCase().startsWith('polkdesq')) store.removeItem(key);
      }
    } catch { /* sessionStorage unavailable — best-effort */ }
    setUser(null);
  }, []);

  // Memoize provider value so consumers re-render only when identity fields change,
  // not on every AuthProvider render.
  const value = useMemo(
    () => ({ user, setUser, updateUser, login, register, logout, loading, sessionMeta }),
    [user, updateUser, login, register, logout, loading, sessionMeta],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
